import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useEnrollLeadInCampaign } from './useCampaignQueue';
import { triggerImmediateEmailProcessing } from '@/utils/triggerImmediateEmailProcessing';
import type { Collaborator } from '@/types/collaborations';

// Helper to sync collaborator to os_contacts
const syncCollaboratorToContacts = async (
  brandId: string,
  collaborator: {
    id: string;
    name: string;
    email?: string | null;
    phone?: string | null;
    company?: string | null;
    job_title?: string | null;
  }
): Promise<string | null> => {
  if (!collaborator.email) return null;

  // Parse name into first/last name
  const nameParts = collaborator.name.trim().split(/\s+/);
  const firstName = nameParts[0] || null;
  const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : null;

  try {
    const { data: contactId, error } = await supabase.rpc('upsert_os_contact', {
      p_brand_id: brandId,
      p_email: collaborator.email,
      p_first_name: firstName,
      p_last_name: lastName,
      p_phone: collaborator.phone || null,
      p_module: ['collaborations'],
      p_activity: ['collaborator'],
      p_organization: collaborator.company || null,
      p_title: collaborator.job_title || null,
      p_user_id: null,
    });

    if (error) {
      console.error('Error syncing collaborator to contacts:', error);
      return null;
    }
    return contactId;
  } catch (err) {
    console.error('Exception syncing collaborator to contacts:', err);
    return null;
  }
};

export const useCollaborators = (brandId: string | null) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const enrollLeadInCampaign = useEnrollLeadInCampaign();

  const { data: collaborators, isLoading } = useQuery({
    queryKey: ['collaborators', brandId],
    queryFn: async () => {
      if (!brandId) return [];
      
      const { data, error } = await supabase
        .from('os_collabs_collaborators')
        .select('*')
        .eq('brand_id', brandId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return (data || []) as unknown as Collaborator[];
    },
    enabled: !!brandId
  });

  const createMutation = useMutation({
    mutationFn: async (collaboratorData: Partial<Collaborator>) => {
      if (!brandId) throw new Error('Brand ID required');
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Clean up UUID fields - convert empty strings to null
      const cleanData: any = {
        name: collaboratorData.name || '',
        email: collaboratorData.email || '',
        brand_id: brandId,
        user_id: user.id,
      };

      // Add collaboration_type_ids as JSON array
      if (collaboratorData.collaboration_type_ids && collaboratorData.collaboration_type_ids.length > 0) {
        cleanData.collaboration_type_ids = collaboratorData.collaboration_type_ids;
      }
      
      // Only add source_lead_id if provided, otherwise leave it null
      if (collaboratorData.source_lead_id && collaboratorData.source_lead_id.trim()) {
        cleanData.source_lead_id = collaboratorData.source_lead_id;
      }

      // Add other optional fields - convert empty strings to null
      if (collaboratorData.phone && collaboratorData.phone.trim()) {
        cleanData.phone = collaboratorData.phone;
      }
      if (collaboratorData.company && collaboratorData.company.trim()) {
        cleanData.company = collaboratorData.company;
      }
      if (collaboratorData.linkedin && collaboratorData.linkedin.trim()) {
        cleanData.linkedin = collaboratorData.linkedin;
      }
      if (collaboratorData.status) {
        cleanData.status = collaboratorData.status;
      }
      if (collaboratorData.metric_type && collaboratorData.metric_type.trim()) {
        cleanData.metric_type = collaboratorData.metric_type;
      }
      if (collaboratorData.source && collaboratorData.source.trim()) {
        cleanData.source = collaboratorData.source;
      }
      if (collaboratorData.medium && collaboratorData.medium.trim()) {
        cleanData.medium = collaboratorData.medium;
      }
      if (collaboratorData.channel && collaboratorData.channel.trim()) {
        cleanData.channel = collaboratorData.channel;
      }
      if (collaboratorData.insights && collaboratorData.insights.trim()) {
        cleanData.insights = collaboratorData.insights;
      }
      if (collaboratorData.remarks && collaboratorData.remarks.trim()) {
        cleanData.remarks = collaboratorData.remarks;
      }

      const { data, error } = await supabase
        .from('os_collabs_collaborators')
        .insert([cleanData])
        .select()
        .single();
      
      if (error) throw error;

      // Sync to os_contacts
      if (data && data.email) {
        const contactId = await syncCollaboratorToContacts(brandId, data);
        if (contactId) {
          await supabase
            .from('os_collabs_collaborators')
            .update({ contact_id: contactId })
            .eq('id', data.id);
        }
      }

      const collaborator = data as unknown as Collaborator;

      // Auto-enroll in active campaigns matching this collaborator's type
      let enrolledCampaigns = 0;
      if (collaborator.collaboration_type_ids && collaborator.collaboration_type_ids.length > 0) {
        try {
          // Get active campaigns that match any of the collaborator's types
          // A campaign matches if its collaboration_type_id is in the collaborator's type array
          // OR if the campaign has no type (brand-wide campaign)
          const { data: campaigns } = await supabase
            .from('os_collabs_campaigns')
            .select('id, name, collaboration_type_id')
            .eq('brand_id', brandId)
            .eq('status', 'active');

          if (campaigns && campaigns.length > 0) {
            for (const campaign of campaigns) {
              // Check if campaign type matches collaborator types OR is brand-wide (no type)
              const isMatch = !campaign.collaboration_type_id || 
                collaborator.collaboration_type_ids.includes(campaign.collaboration_type_id);
              
              if (!isMatch) continue;

              // Get campaign emails
              const { data: campaignEmails } = await supabase
                .from('os_collabs_emails')
                .select('id, sequence_order, delay_days, delay_hours, send_conditions')
                .eq('campaign_id', campaign.id)
                .order('sequence_order', { ascending: true });

              if (campaignEmails && campaignEmails.length > 0) {
                const nameParts = (collaborator.name || '').split(' ');
                const firstName = nameParts[0] || '';
                const lastName = nameParts.slice(1).join(' ') || '';

                await enrollLeadInCampaign.mutateAsync({
                  brandId: brandId,
                  sourceModule: 'collaborations',
                  campaignId: campaign.id,
                  recipientId: collaborator.id,
                  recipientEmail: collaborator.email,
                  recipientName: collaborator.name,
                  recipientData: {
                    first_name: firstName,
                    last_name: lastName,
                    email: collaborator.email,
                    company: collaborator.company || '',
                    job_title: collaborator.job_title || '',
                    website: collaborator.website || '',
                    linkedin: collaborator.linkedin || '',
                  },
                  campaignEmails: campaignEmails.map(e => ({
                    id: e.id,
                    sequence_order: e.sequence_order,
                    delay_days: e.delay_days,
                    delay_hours: e.delay_hours,
                    send_conditions: e.send_conditions as Record<string, unknown> | null,
                  })),
                });
                enrolledCampaigns++;
                console.log(`[useCollaborators] Enrolled collaborator ${collaborator.email} in campaign "${campaign.name}"`);
              }
            }
          }

          // Trigger immediate processing if enrolled in campaigns (no 5-min wait)
          if (enrolledCampaigns > 0) {
            await triggerImmediateEmailProcessing(brandId);
          }
        } catch (enrollError) {
          console.error('[useCollaborators] Failed to enroll collaborator in campaigns:', enrollError);
          // Don't fail the collaborator creation if enrollment fails
        }
      }

      return collaborator;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['collaborators', brandId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-queue'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-queue-summary'] });
      toast({ 
        title: "Success - Collaborator Created",
        description: "Collaborator created successfully" 
      });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Collaborator> & { id: string }) => {
      // Clean up UUID fields - convert empty strings to null
      const cleanUpdates: any = { ...updates };
      
      // Handle collaboration_type_ids array - always keep as array, never null
      if ('collaboration_type_ids' in cleanUpdates) {
        if (!Array.isArray(cleanUpdates.collaboration_type_ids)) {
          cleanUpdates.collaboration_type_ids = [];
        }
        // Ensure it's a valid array of strings (UUIDs)
        cleanUpdates.collaboration_type_ids = cleanUpdates.collaboration_type_ids.filter((id: any) => id && typeof id === 'string');
        // Keep empty arrays as empty arrays, don't convert to null
      }
      
      // Handle UUID fields - convert empty strings to null
      if ('source_lead_id' in cleanUpdates) {
        if (!cleanUpdates.source_lead_id || cleanUpdates.source_lead_id.trim() === '') {
          cleanUpdates.source_lead_id = null;
        }
      }
      
      // Handle optional text fields - convert empty strings to null for optional fields
      const optionalFields = ['phone', 'company', 'linkedin', 'insights', 'remarks', 'metric_type', 'source', 'medium', 'channel'];
      optionalFields.forEach(field => {
        if (field in cleanUpdates && cleanUpdates[field] === '') {
          cleanUpdates[field] = null;
        }
      });
      
      const { data, error } = await supabase
        .from('os_collabs_collaborators')
        .update(cleanUpdates)
        .eq('id', id)
        .select('*')
        .single();
      
      if (error) throw error;

      // Sync to os_contacts
      if (data && data.email) {
        const contactId = await syncCollaboratorToContacts(brandId!, data);
        if (contactId) {
          await supabase
            .from('os_collabs_collaborators')
            .update({ contact_id: contactId })
            .eq('id', data.id);
        }
      }

      return data as unknown as Collaborator;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['collaborators', brandId] });
      toast({ 
        title: "Success - Collaborator Updated", 
        description: "Collaborator updated successfully" 
      });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('os_collabs_collaborators')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collaborators', brandId] });
      toast({ title: "Success", description: "Collaborator deleted successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  const syncAllToContacts = async (): Promise<number> => {
    if (!brandId || !collaborators) return 0;

    let synced = 0;
    for (const collaborator of collaborators) {
      if (!collaborator.email) continue;
      const contactId = await syncCollaboratorToContacts(brandId, collaborator);
      if (contactId) {
        await supabase
          .from('os_collabs_collaborators')
          .update({ contact_id: contactId })
          .eq('id', collaborator.id);
        synced++;
      }
    }
    queryClient.invalidateQueries({ queryKey: ['collaborators', brandId] });
    return synced;
  };

  return {
    collaborators: collaborators || [],
    isLoading,
    createCollaborator: createMutation.mutate,
    updateCollaborator: updateMutation.mutateAsync,
    deleteCollaborator: deleteMutation.mutate,
    syncAllToContacts,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
